import { Modal } from 'react-bootstrap';
import { useState, useEffect } from 'react';
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import '@cyntler/react-doc-viewer/dist/index.css';
import 'styles/components/_stylishViewFileDialog.scss';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { isRunningInTeams } from 'utils/isRunningInTeams';

interface StylishViewFileDialogProps {
  onClose: () => void;
  show: boolean;
  fileUrl: string;
  fileName: string | null;
  page?: number;
  mimetype?: string;
  /** When set, shows a download control in the modal header */
  onDownload?: () => void;
  downloadLabel?: string;
}

type FileType = 'pdf' | 'office' | 'text' | 'other';

/**
 * CSS styles injected into text file iframes to ensure readability. This must
 * be injected via JavaScript since it styles content inside the iframe's
 * document.
 */
const TEXT_FILE_STYLES = `
  body {
    color: #000 !important;
    background-color: #fff !important;
    padding: 20px;
    font-family: monospace;
    white-space: pre-wrap;
    word-wrap: break-word;
  }
  pre {
    color: #000 !important;
    background-color: #fff !important;
    white-space: pre-wrap;
    word-wrap: break-word;
  }
`;

/** Determines the file type from fileName or mimetype */
function getFileType(fileName: string | null, mimetype?: string): FileType {
  // Check mimetype first
  if (mimetype) {
    if (mimetype.includes('pdf')) return 'pdf';
    if (
      mimetype.includes('word') ||
      mimetype.includes('document') ||
      mimetype.includes('presentation') ||
      mimetype.includes('powerpoint') ||
      mimetype.includes('sheet') ||
      mimetype.includes('excel')
    ) {
      return 'office';
    }
    if (mimetype.includes('text')) return 'text';
  }

  // Fallback to file extension
  const extension = fileName?.toLowerCase();
  if (extension?.endsWith('.pdf')) return 'pdf';
  if (extension?.match(/\.(docx?|xlsx?|pptx?)$/)) return 'office';

  // Text-based files: plain text, CSV, code files, and other text formats
  if (
    extension?.match(
      /\.(txt|csv|msg|url|js|ts|py|java|md|json|html|css|c|cpp|cs|go|php|rb|sh|tex)$/
    )
  ) {
    return 'text';
  }

  return 'other';
}

// ============================================================================
// Separate Components
// ============================================================================

interface PdfBodyProps {
  fileUrl: string;
  page?: number;
  fileName?: string | null;
}

/** Renders PDF viewer with page navigation support */
function PdfBody({ fileUrl, page, fileName }: PdfBodyProps) {
  const pdfUrl = page && page > 0 ? `${fileUrl}#page=${page}` : fileUrl;

  // Teams hosts the app in a sandboxed iframe where Chrome refuses to run its
  // built-in PDF plugin, so the embedded viewer renders "blocked by Chrome".
  // Opening a top-level window is the only reliable way to show the PDF there.
  if (isRunningInTeams()) {
    return <TeamsPdfBody pdfUrl={pdfUrl} fileName={fileName} />;
  }

  return (
    <Modal.Body>
      <iframe
        src={pdfUrl}
        title={fileName || 'PDF Document'}
        className="pdf-viewer-iframe"
      />
    </Modal.Body>
  );
}

interface TeamsPdfBodyProps {
  pdfUrl: string;
  fileName?: string | null;
}

/** Teams fallback: launch the PDF in a separate browser window. */
function TeamsPdfBody({ pdfUrl, fileName }: TeamsPdfBodyProps) {
  const openInNewWindow = () => {
    // Must run inside the click handler so the popup blocker allows it.
    window.open(pdfUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Modal.Body className="d-flex flex-column align-items-center justify-content-center gap-3 text-center">
      <SharedIcon iconName="picture_as_pdf" size="lg" />
      <div>
        <div className="fw-bold">{fileName || 'PDF Document'}</div>
        <div className="text-muted">
          Microsoft Teams cannot display PDFs inline. Open it in a new browser
          window to view or print it.
        </div>
      </div>
      <StylishNewButton
        type="button"
        className="button button--primary"
        onClick={openInNewWindow}
      >
        <SharedIcon iconName="open_in_new" size="sm" classes="me-1" />
        Open PDF in New Window
      </StylishNewButton>
    </Modal.Body>
  );
}

interface TextBodyProps {
  fileUrl: string;
  fileName?: string | null;
}

/**
 * Renders text file viewer with CSS injection for readability. For CSV files,
 * fetches content and displays in pre tag to prevent download. For other text
 * files, uses iframe with CSS injection.
 */
function TextBody({ fileUrl, fileName }: TextBodyProps) {
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isCsvFile = fileName?.toLowerCase().endsWith('.csv');

  useEffect(() => {
    if (!isCsvFile) return;

    // Fetch CSV content as text to prevent browser download
    fetch(fileUrl)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load file');
        return res.text();
      })
      .then((text) => {
        setContent(text);
        setError(null); // Clear any previous errors
      })
      .catch((err) => {
        console.error('Failed to fetch CSV file:', err);
        setError('Failed to load file content');
        setContent(null);
      });
  }, [fileUrl, isCsvFile]);

  // Derive loading state from content and error (avoids setState in effect)
  const isLoading = isCsvFile && !content && !error;

  // For CSV files, display fetched content in pre tag
  if (isCsvFile) {
    return (
      <Modal.Body>
        {isLoading && (
          <div className="p-4 text-center">Loading file content...</div>
        )}
        {error && <div className="text-danger p-4 text-center">{error}</div>}
        {content && (
          <pre
            style={{
              color: '#000',
              backgroundColor: '#fff',
              padding: '20px',
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              overflow: 'auto',
              maxHeight: '100%',
              margin: 0,
            }}
          >
            {content}
          </pre>
        )}
      </Modal.Body>
    );
  }

  // For other text files, use iframe with CSS injection
  return (
    <Modal.Body>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <iframe
        src={fileUrl}
        title={fileName || 'Text File'}
        className="text-viewer-iframe"
        onLoad={(e) => {
          try {
            const iframe = e.target as HTMLIFrameElement;
            const iframeDoc =
              iframe.contentDocument || iframe.contentWindow?.document;
            if (iframeDoc) {
              const style = iframeDoc.createElement('style');
              style.textContent = TEXT_FILE_STYLES;
              iframeDoc.head.appendChild(style);
            }
          } catch (err) {
            // Cross-origin restrictions may prevent styling, but file should still display
            console.warn('Could not inject text file styles:', err);
          }
        }}
      />
    </Modal.Body>
  );
}

interface OfficeBodyProps {
  fileUrl: string;
  fileName?: string | null;
}

/** Renders office documents and other files using DocViewer */
function OfficeBody({ fileUrl, fileName }: OfficeBodyProps) {
  const documents = [{ uri: fileUrl, fileName: fileName || undefined }];
  const docViewerConfig = {
    header: {
      disableHeader: true,
      disableFileName: true,
    },
  };

  return (
    <Modal.Body className="office-doc-body">
      <div className="office-doc-wrapper">
        <DocViewer
          documents={documents}
          pluginRenderers={DocViewerRenderers}
          config={docViewerConfig}
          className="office-doc-viewer"
        />
      </div>
    </Modal.Body>
  );
}

/**
 * Consolidated file viewer dialog that handles multiple file types:
 *
 * - PDFs: Uses iframe with native browser viewer (supports #page= navigation)
 * - Office docs (DOCX, XLSX, PPTX): Uses DocViewer
 * - Text files: Uses iframe with CSS injection for readability
 * - Other files: Uses DocViewer (images, videos, etc.)
 */
export default function StylishViewFileDialog({
  onClose,
  show,
  fileUrl,
  fileName,
  page,
  mimetype,
  onDownload,
  downloadLabel = 'Download',
}: StylishViewFileDialogProps) {
  const fileType = getFileType(fileName, mimetype);

  /** Gets modal title with optional page indicator for PDFs */
  const modalTitle =
    fileType === 'pdf' && page ? (
      <>
        {fileName}
        <span className="text-muted"> (Page {page})</span>
      </>
    ) : (
      fileName
    );

  /** Renders appropriate body content based on file type */
  const renderBody = () => {
    switch (fileType) {
      case 'pdf':
        return <PdfBody fileUrl={fileUrl} page={page} fileName={fileName} />;
      case 'text':
        return <TextBody fileUrl={fileUrl} fileName={fileName} />;
      case 'office':
      case 'other':
      default:
        return <OfficeBody fileUrl={fileUrl} fileName={fileName} />;
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      backdrop="static"
      keyboard={true}
      fullscreen={true}
      className="stylish-view-file-dialog"
    >
      <Modal.Header closeButton closeVariant="white">
        <div className="d-flex align-items-center me-2 min-w-0 flex-grow-1 gap-2">
          <Modal.Title className="text-truncate mb-0 flex-grow-1">
            {modalTitle}
          </Modal.Title>
          {onDownload ? (
            <StylishNewButton
              type="button"
              className="button button--secondary flex-shrink-0"
              onClick={onDownload}
            >
              <SharedIcon iconName="download" size="sm" classes="me-1" />
              {downloadLabel}
            </StylishNewButton>
          ) : null}
        </div>
      </Modal.Header>
      {renderBody()}
    </Modal>
  );
}
